import { AnimatePresence, motion } from "framer-motion";
import { Check, SlidersHorizontal, X } from "lucide-react";
import PropTypes from "prop-types";
import { cn } from "../helpers";

const PANEL_WIDTH = 360;

export function FilterToggle({ open, count = 0, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      aria-controls="page-filter-panel"
      aria-keyshortcuts="F"
      title="Filter (tekan F)"
      className={`relative inline-flex h-10 items-center gap-2 rounded-lg border px-3.5 text-sm font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-slate-300 ${
        open || count > 0
          ? "border-slate-400 bg-slate-100 text-slate-800"
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      <SlidersHorizontal size={16} />
      <span>Filter</span>
      {count > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-slate-800 px-1.5 text-xs font-semibold text-white">
          {count}
        </span>
      )}
      <kbd className="hidden rounded border border-slate-300 bg-white px-1.5 text-xs font-medium text-slate-500 lg:inline-block">
        F
      </kbd>
    </button>
  );
}
FilterToggle.propTypes = {
  open: PropTypes.bool.isRequired,
  count: PropTypes.number,
  onClick: PropTypes.func.isRequired,
};

export function ActiveFilterChips({ chips }) {
  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap items-center gap-2" aria-label="Filter aktif">
      {chips.map(({ key, label, dot, onRemove }) => (
        <span
          key={key}
          className="inline-flex max-w-full items-center gap-2 rounded-full bg-slate-100 py-1 pl-3 pr-1.5 text-sm font-medium text-slate-700"
        >
          {dot && <span className={`size-2 shrink-0 rounded-full ${dot}`} />}
          <span className="truncate">{label}</span>
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Hapus filter ${label}`}
            className="flex size-5 shrink-0 items-center justify-center rounded-full hover:bg-white/80"
          >
            <X size={14} />
          </button>
        </span>
      ))}
    </div>
  );
}
ActiveFilterChips.propTypes = {
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.node.isRequired,
      dot: PropTypes.string,
      onRemove: PropTypes.func.isRequired,
    }),
  ).isRequired,
};

export function FilterOptionList({ value, onChange, options, label }) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={String(option.value)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={`flex h-11 items-center gap-3 rounded-xl border px-3.5 text-left text-sm transition focus-visible:ring-2 focus-visible:ring-slate-300 ${
              selected
                ? "border-slate-400 bg-slate-100 font-semibold text-slate-800"
                : "border-slate-200 bg-white font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            <span className={`size-2.5 shrink-0 rounded-full ${option.colorClass}`} />
            <span className="flex-1 truncate">{option.label}</span>
            <span
              className={`flex size-5 shrink-0 items-center justify-center rounded-full border transition ${
                selected ? "border-slate-700 bg-slate-700 text-white" : "border-slate-300"
              }`}
            >
              {selected && <Check size={12} strokeWidth={3} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}
FilterOptionList.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any,
      label: PropTypes.node,
      colorClass: PropTypes.string,
    }),
  ).isRequired,
  label: PropTypes.string.isRequired,
};

export function DockedFilterLayout({ children, className }) {
  return (
    <div className={cn("flex h-full min-h-0 min-w-0 overflow-x-clip", className)}>{children}</div>
  );
}
DockedFilterLayout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

export function DockedFilterPanel({
  open,
  overlay,
  title,
  count = 0,
  onClose,
  onReset,
  onApply,
  children,
}) {
  const panelClass = overlay
    ? "leave-filter-panel fixed bottom-0 right-0 top-[90px] z-30 w-[min(360px,100vw)] border-l border-slate-200 bg-white shadow-xl"
    : "leave-filter-panel h-full shrink-0 overflow-hidden border-l border-slate-200 bg-white";
  const motionProps = overlay
    ? {
        initial: { x: "100%" },
        animate: { x: 0 },
        exit: { x: "100%" },
      }
    : {
        initial: { width: 0 },
        animate: { width: PANEL_WIDTH },
        exit: { width: 0 },
      };

  return (
    <AnimatePresence initial={false}>
      {open && overlay && (
        <motion.div
          key="filter-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 top-22.5 z-20 bg-black/20"
          onClick={onClose}
        />
      )}
      {open && (
        <motion.aside
          key="filter-panel"
          id="page-filter-panel"
          aria-label={`Filter ${title.toLowerCase()}`}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className={panelClass}
          {...motionProps}
        >
          <div className="flex h-full flex-col" style={{ width: "min(360px, 100vw)" }}>
            <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                <SlidersHorizontal size={20} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base font-semibold text-slate-900">Filter {title}</h2>
                <p className="text-sm text-slate-500">
                  {count > 0 ? `${count} filter aktif` : "Belum ada filter aktif"}
                </p>
              </div>
              <kbd className="hidden rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-500 lg:inline-block">
                Esc
              </kbd>
              <button
                type="button"
                onClick={onClose}
                aria-label="Tutup filter"
                title="Tutup (Esc)"
                className="flex size-10 shrink-0 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
              >
                <X size={20} />
              </button>
            </div>

            <div className="leave-filter-content flex flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden p-5">
              {children}
            </div>

            <div className="flex gap-3 border-t border-slate-200 bg-white px-5 py-4">
              <button
                type="button"
                onClick={onReset}
                className="h-11 flex-1 rounded-lg border border-slate-300
                  bg-white text-sm font-medium text-slate-600
                  transition hover:border-slate-400 hover:bg-[#E3E6FB]
                  hover:text-[#0E3679]
                  focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-[#0E3679]/30"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={onApply}
                className="h-11 flex-[1.4] rounded-lg
                  bg-[#0E3679] text-sm font-semibold text-white
                  transition hover:bg-[#0B2B61]
                  focus-visible:outline-none focus-visible:ring-2
                  focus-visible:ring-[#0E3679]/40
                  focus-visible:ring-offset-2"
              >
                Terapkan Filter
              </button>
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
DockedFilterPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  overlay: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  count: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  children: PropTypes.node,
};
